import React, { useMemo } from 'react';
import { ArrowDownTrayIcon, TableCellsIcon } from '@heroicons/react/20/solid';
import ChartCard, { ChartCardState } from '@ui/chart/ChartCard';
import ChartMessage from '@ui/chart/ChartMessage';
import { CHART_HEIGHT } from '@ui/chart/chartTokens';
import DataTable, { DataTableRowLimit } from '@ui/chart/DataTable';
import BarChart from '@ui/chart/BarChart';
import LineChart from '@ui/chart/LineChart';
import KpiTile from '@ui/chart/KpiTile';
import { barChartTable, lineChartTable } from '@ui/chart/chartTable';
import { formatSigned, formatValue } from '@ui/chart/chartFormat';
import ButtonTabs from '@ui/tabs/ButtonTabs';
import CheckboxMenu from '@ui/menu/CheckboxMenu';
import ToolbarButton from '@ui/button/ToolbarButton';
import FormButton from '@ui/button/FormButton';
import { MonthRange } from '@ui/selector/MonthRangePicker';
import { GuestMixEvolution, GuestMixSplit, RevenueScope, VatBasis } from '@app/model/bi/Bi';
import { formatDate, MONTH_LABELS, missingNote, missingOf, revenueContent, revenueName } from '../Bi.utilities';
import {
	ColumnSet, columnsOf, EVOLUTION_LINES, EvolutionLine, EvolutionMeasure, evolutionLines, focusFigures, GuestMixTable, isFixed, MIX_BARS, mixBars,
	pointOf, ROW_LIMIT, shareLines, splitInfo, tableRows, tileFigures, tileLines, totalRow,
} from './GuestMix.utilities';

// The period of the page in words: "Jan – Dec 2026", and its first and last night.
const lastDay = (yearMonth: string) => new Date(Date.UTC(Number(yearMonth.slice(0, 4)), Number(yearMonth.slice(5, 7)), 0)).toISOString().slice(0, 10);
export const periodLabel = (period: MonthRange): string => {
	const m = (ym: string) => MONTH_LABELS[Number(ym.slice(5, 7)) - 1];
	if (period.from === period.to) return `${m(period.from)} ${period.from.slice(0, 4)}`;
	return period.from.slice(0, 4) === period.to.slice(0, 4) ? `${m(period.from)} – ${m(period.to)} ${period.to.slice(0, 4)}`
		: `${m(period.from)} ${period.from.slice(0, 4)} – ${m(period.to)} ${period.to.slice(0, 4)}`;
};
const nightsLabel = (period: MonthRange): string => `${formatDate(`${period.from}-01`)} – ${formatDate(lastDay(period.to))}`;

export const COLUMN_SETS: { value: ColumnSet, label: string }[] = [
	{ value: 'ly', label: 'Previous year (STLY, LY actual, Δ%, MIX Δ)' },
	{ value: 'd1', label: 'Situation D-1' }, { value: 'd3', label: 'Situation D-3' }, { value: 'd7', label: 'Situation D-7' },
	{ value: 'd14', label: 'Situation D-14' }, { value: 'd30', label: 'Situation D-30' },
	{ value: 'pu1', label: 'PU 1' }, { value: 'pu3', label: 'PU 3' }, { value: 'pu7', label: 'PU 7' }, { value: 'pu14', label: 'PU 14' }, { value: 'pu30', label: 'PU 30' },
];

// ROO2-272 D2: the words of "Cancelled, no guest data" in the (i) of the two splits that have it.
const NO_DATA_INFO = ' Cancelled, no guest data = a booking that the PMS sent only as a cancellation: 5stelle sends no guests and no nationality for it.';
const INFO: Record<GuestMixSplit, string> = {
	GUEST_TYPE: `Solo = 1 adult, Couple = 2 adults, Family = one child or more, 3+ adults = 3 adults or more and no child. Infants do not change the type. Unknown = no adult on the booked room.${NO_DATA_INFO}`,
	NATIONALITY: `The country code that the PMS puts on the guest of the booked room. Unknown = no code.${NO_DATA_INFO}`,
	LENGTH_OF_STAY: 'All the nights of the booked room, also the nights outside the stay months. Only the nights inside the months count in the figures.',
	LEAD_TIME: 'The days from the book day to the check-in day of the booked room, in the time of the company. Unknown = no book day, or a book day after the check-in day.',
};

// --- headline figures (decision 16) ---------------------------------------------------------------------------------------

type Headline = { gain: { title: string, mix: number | null, points: number } | null, loss: { title: string, mix: number | null, points: number } | null };

// Two KpiTiles in the tile grid of docs/CHARTS.md (one column below the sm width): a KpiStrip puts the two items side by side
// on a phone and cuts a long value name ("Biggest gain · United Kingdom") — found in the UI check of 2026-10-21.
// lfl (ROO2-271 D1): MIX Δ compares today like-for-like, so the label says it.
export const GuestMixHeadline: React.FunctionComponent<{ headline: Headline | undefined, lfl: boolean }> = ({ headline, lfl }) => {
	const vs = lfl ? 'vs STLY, like-for-like' : 'vs STLY';
	const tile = (word: string, h: Headline['gain'] | undefined) => <KpiTile key={word}
		label={h === undefined || h === null ? `${word} ${vs}` : `${word} · ${h.title}`} value={h?.mix ?? null} format="percent" decimals={1}
		state={h === undefined ? 'loading' : 'default'}
		change={h === undefined || h === null ? undefined : { value: h.points, format: 'points', label: vs, upIsGood: null }} />;
	return <div aria-label="Guest Mix: headline figures" className="grid grid-cols-1 gap-4 sm:grid-cols-2">
		{tile('Biggest gain', headline === undefined ? undefined : headline.gain)}
		{tile('Biggest loss', headline === undefined ? undefined : headline.loss)}
	</div>;
};

// --- the mix (direction B) --------------------------------------------------------------------------------------------------

type MixProps = { table: GuestMixTable | undefined, split: GuestMixSplit, period: MonthRange, state: ChartCardState, onRetry: () => void, error: string | null };

// The share of the room nights of the first rows of the table, this year against STLY.
export const GuestMixMixCard: React.FunctionComponent<MixProps> = ({ table, split, period, state, onRetry, error }) => {
	const data = useMemo(() => table === undefined ? null : mixBars(table), [table]);
	const empty = table !== undefined && table.rows.length === 0;
	const label = splitInfo(split).label;
	return <ChartCard title={`Mix by ${label.toLowerCase()} · this year and STLY`} period={`${periodLabel(period)} · share of the room nights`}
		state={empty ? 'empty' : state} onRetry={onRetry} emptyLabel="Nothing on the books for this period, this year or last."
		errorLabel={error === null ? 'Could not load.' : `Could not load: ${error}`}
		table={data === null ? undefined : <DataTable {...barChartTable({ ...data, categoryLabel: label, format: 'percent', decimals: 1 })} framed={false} ariaLabel="Mix" />}
		caption={`${table !== undefined && table.rows.length > MIX_BARS
			? `One pair of bars for each of the first ${MIX_BARS} rows of the table${isFixed(split) ? '' : ', largest first'}; Other = the rest added together.`
			: 'One pair of bars for each row of the table.'} STLY = the same stay dates last year, as they stood one year ago today.`}>
		{data !== null && <BarChart categories={data.categories} series={data.series} format="percent" decimals={1} ariaLabel={`Mix by ${label.toLowerCase()}`} />}
	</ChartCard>;
};

// --- the table (brief 4.6, decisions 6–8) -------------------------------------------------------------------------------------

type TableProps = {
	table: GuestMixTable | undefined, split: GuestMixSplit, period: MonthRange, sets: ColumnSet[], onToggle: (set: ColumnSet) => void,
	scope: RevenueScope, vat: VatBasis, state: ChartCardState, onRetry: () => void, resetKey: string, lastYearNote: string | null,
	// ROO2-271 D1: the scope has a property with no stays in the last-year period (TODAY LFL RN, REV and MIX %).
	lfl: boolean,
	// The Excel file (ROO2-261): onExport downloads it; exporting = the file loads ("Preparing…"); exportReady = the table
	// shows its period (not while it loads, refreshes or failed).
	onExport: () => void, exporting: boolean, exportReady: boolean,
	error: string | null,
	// ROO2-315 part c2 (Q4): the ADR and the report rules of the scope, once (adrRulesNote); null without rules.
	rulesNote?: string | null,
};

const LIMIT: DataTableRowLimit = {
	count: ROW_LIMIT,
	moreLabel: (hidden, _all, level) => level === 0 ? `+ ${hidden} more, included in the total` : `+ ${hidden} more properties, included in the row`,
	showAllLabel: (all, level) => level === 0 ? `Show all ${all}` : `Show all ${all} properties`,
	showTopLabel: `Show the top ${ROW_LIMIT}`,
};

export const GuestMixTableCard: React.FunctionComponent<TableProps> = ({
	table, split, period, sets, onToggle, scope, vat, state, onRetry, resetKey, lastYearNote, lfl, onExport, exporting, exportReady, error, rulesNote = null,
}) => {
	const { columns, groups } = useMemo(() => columnsOf(split, sets, lfl), [split, sets, lfl]);
	const rows = useMemo(() => table === undefined ? [] : tableRows(table, scope, vat), [table, scope, vat]);
	const empty = table !== undefined && table.rows.length === 0;
	const missing = table === undefined ? null : missingNote(missingOf([table.total.cuts[0], table.total.stly, table.total.ly], scope, vat), '–', 'the period');
	const info = splitInfo(split);
	return <ChartCard title={`${info.label} · ${periodLabel(period)}`}
		period={`Stay dates ${nightsLabel(period)} · past nights actual, later nights on the books`}
		info={{ title: info.label, content: INFO[split] }}
		state={empty ? 'empty' : state} onRetry={onRetry} emptyLabel="Nothing on the books for this period, this year or last."
		errorLabel={error === null ? 'Could not load.' : `Could not load: ${error}`}
		actions={<>
			{/* ToolbarButton (design system v1.19.0): the 36 px look of the Columns button beside it, as the Segmentation export. */}
			{rows.length > 0 && <ToolbarButton icon={<ArrowDownTrayIcon className="h-4 w-4" />} disabled={!exportReady} onClick={onExport}>
				{exporting ? 'Preparing…' : 'Export'}
			</ToolbarButton>}
			<CheckboxMenu label={<><TableCellsIcon aria-hidden="true" className="h-4 w-4 text-gray-400" />Columns<span className="font-normal text-gray-500">· {sets.length}</span></>}
				options={COLUMN_SETS} selected={sets} onToggle={onToggle} anchor="bottom end" variant="button" />
		</>}
		caption={<>
			Total = the total of the Segmentation report for the same stay dates. RN = sold room nights; MIX % = the share of the room nights;
			REV = {revenueName(scope, vat)} ({revenueContent(scope)}). STLY = the same stay dates last year, as they stood one year ago today;
			LY actual = as they stand today. Δ% compares {lfl ? 'TODAY LFL REV' : 'revenue'} with STLY REV and LY actual REV; MIX Δ = {lfl ? 'TODAY LFL MIX %' : 'MIX %'} − STLY MIX %, in points. PU n = today − situation D-n;
			ADR Δ = ADR today − ADR at situation D-n. ADR includes the €0 nights (complimentary, house use).
			Each booked room is in one row.{lastYearNote === null ? '' : ` ${lastYearNote}`}{missing === null ? '' : ` ${missing}`}
			{rulesNote === null ? null : <><br />{rulesNote}</>}
		</>}>
		{table !== undefined && <DataTable key={resetKey} ariaLabel={info.label} framed={false} stickyHeader columns={columns} columnGroups={groups} rows={rows}
			defaultSort={isFixed(split) ? undefined : { columnId: 'rn', direction: 'desc' }} sortCycle="threeClicks" rowLimit={LIMIT}
			footer={rows.length > 1 ? totalRow(table, scope, vat) : undefined} />}
	</ChartCard>;
};

// --- the evolution (ROO2-265, brief 4.3) ------------------------------------------------------------------------------------

export type EvolutionView = 'BUILD_UP' | 'SHARE';
export const WINDOWS = [30, 90, 180, 365];

type EvolutionProps = {
	evolution: GuestMixEvolution | undefined, table: GuestMixTable | undefined, split: GuestMixSplit, period: MonthRange,
	view: EvolutionView, onView: (v: EvolutionView) => void, measure: EvolutionMeasure, onMeasure: (m: EvolutionMeasure) => void,
	window: number, onWindow: (w: number) => void, selected: string | null, onSelect: (key: string | null) => void,
	scope: RevenueScope, vat: VatBasis, state: ChartCardState, error: string | null, onRetry: () => void,
};

const formatOf = (measure: EvolutionMeasure) => measure === 'rn' ? 'number' as const : 'money' as const;

export const GuestMixEvolutionCard: React.FunctionComponent<EvolutionProps> = ({
	evolution, table, split, period, view, onView, measure, onMeasure, window, onWindow, selected, onSelect, scope, vat, state, error, onRetry,
}) => {
	const lines = useMemo(() => evolution === undefined || table === undefined ? null : evolutionLines(evolution, table), [evolution, table]);
	const lastNight = lastDay(period.to);
	const finished = evolution !== undefined && lastNight < evolution.days[0];
	const nothing = lines !== null && lines.every(l => l.thisYear.every(f => f.roomNights === 0) && l.lastYear.every(f => f.roomNights === 0));
	const label = splitInfo(split).label;
	// ADR is not a share: the Share view offers RN and Revenue only (brief 3).
	const shareMeasure: 'rn' | 'rev' = measure === 'adr' ? 'rev' : measure;
	const share = useMemo(() => lines === null || evolution === undefined ? null
		: shareLines(lines, shareMeasure, scope, vat, evolution.days.length).map(s => ({ ...s, values: s.values.map((v, i) => evolution.days[i] > lastNight ? null : v) })),
	[lines, shareMeasure, scope, vat, evolution, lastNight]);
	const days = evolution?.days ?? [];
	const range = days.length === 0 ? 'As it stood on each day of the window' : `As it stood on each day ${formatDate(days[0])} – ${formatDate(days[days.length - 1])}`;
	// ROO2-271 D2: a tile shows the change inside the window, so the window changes the numbers.
	const change = days.length === 0 ? 'The change in the window' : `The change from ${formatDate(days[0])} to ${formatDate(days[days.length - 1])}`;
	return <ChartCard title={view === 'BUILD_UP' ? `How each built up · ${label}` : `How the mix moved · ${label}`}
		period={view === 'BUILD_UP'
			? (measure === 'adr' ? `ADR on the books now, and its change in the window. ${range}` : `${change}, against the same days last year`)
			: `Share of the ${shareMeasure === 'rn' ? 'room nights' : 'revenue'} on the books. ${range}`}
		info={{ title: view === 'BUILD_UP' ? 'Build-up' : 'Share', content: view === 'BUILD_UP'
			? 'Each tile: the room nights (or the revenue) that the row gained in the window, net of cancellations, against the same days last year (the same stay dates one year earlier, as they stood one year earlier). The title gives the figure on the books now. With ADR: the ADR now and its change in the window. The line: the figure on the books on each day. A click on a tile opens its chart.'
			: 'The share of each row on each day of the window. The lines of one day add up to 100 %.' }}
		state={state} onRetry={onRetry} errorLabel={error === null ? 'Could not load.' : `Could not load: ${error}`}
		table={view === 'SHARE' && share !== null && !finished && !nothing ? <DataTable {...lineChartTable({ dates: days, series: share, format: 'percent', decimals: 1 })}
			framed={false} size="normal" ariaLabel="Share of each row" /> : undefined}
		caption={<>{view === 'BUILD_UP'
			? 'One tile for each of the first 8 rows of the table (Unknown and Cancelled, no guest data are in the table only).'
			: (lines !== null && lines.length > EVOLUTION_LINES
				? `One line for each of the first ${EVOLUTION_LINES} rows of the table; Other = the rest added together (gray).`
				: 'One line for each row of the table.')}</>}>
		{/* The three controls of the card: one row from the sm width, a stack below it (the actions row of ChartCard does not wrap). */}
		<div className="mb-4 grid grid-cols-1 gap-2 sm:flex sm:flex-wrap sm:items-center sm:gap-x-6">
			<ButtonTabs label="View of the evolution" value={view} onChange={v => onView(v as EvolutionView)}
				options={[{ value: 'BUILD_UP', label: 'Build-up' }, { value: 'SHARE', label: 'Share' }]} />
			<ButtonTabs label="Measure of the evolution" value={view === 'SHARE' ? shareMeasure : measure} onChange={v => onMeasure(v as EvolutionMeasure)}
				options={[{ value: 'rn', label: 'RN' }, { value: 'rev', label: 'Revenue' }, ...(view === 'BUILD_UP' ? [{ value: 'adr', label: 'ADR' }] : [])]} />
			<ButtonTabs label="Window of the evolution" value={String(window)} onChange={v => onWindow(Number(v))}
				options={WINDOWS.map(w => ({ value: String(w), label: `${w} days` }))} />
		</div>
		{/* A finished period or an empty window: the message under the controls, so the window can be widened (the empty
		    state of ChartCard would hide the controls; developer decision of 2026-10-21). */}
		{(finished || nothing) && <ChartMessage height={CHART_HEIGHT.normal}>
			{finished ? 'This stay period had finished before the window starts. Widen the window.' : 'Nothing on the books in this window.'}
		</ChartMessage>}
		{!finished && !nothing && <div>
			{view === 'BUILD_UP' && lines !== null && <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
				{tileLines(lines).map(l => <BuildUpTile key={l.key} line={l} measure={measure} scope={scope} vat={vat} lastNight={lastNight} days={days}
					selected={selected === l.key} onClick={() => onSelect(selected === l.key ? null : l.key)} />)}
			</div>}
			{view === 'SHARE' && share !== null && <LineChart dates={days} series={share} format="percent" decimals={1} ariaLabel={`Share of each ${label.toLowerCase()}`} />}
		</div>}
	</ChartCard>;
};

// A signed figure with the minus sign of the chips (−), not a hyphen.
const signedText = (value: number | null, format: 'number' | 'money', decimals: number) =>
	formatSigned(value, format, { decimals }).replace(/^-/, '−');

const BuildUpTile: React.FunctionComponent<{ line: EvolutionLine, measure: EvolutionMeasure, scope: RevenueScope, vat: VatBasis, lastNight: string, days: string[],
	selected: boolean, onClick: () => void }> = ({ line, measure, scope, vat, lastNight, days, selected, onClick }) => {
	const ty = line.thisYear.map((f, i) => days[i] > lastNight ? null : pointOf(f, measure, scope, vat));
	const ly = line.lastYear.map(f => pointOf(f, measure, scope, vat));
	const fig = tileFigures(ty, ly, measure);
	// ROO2-271 D2 (canvas direction A): ADR = the ADR now, its change in the window gray beside last year's change.
	if (measure === 'adr') {
		return <KpiTile label={line.title} value={fig.now} format="money" decimals={2} trend={ty}
			change={fig.gained === null ? undefined : { value: fig.gained, format: 'money', upIsGood: null,
				label: fig.lastYearGained === null ? 'in the window' : `vs ${signedText(fig.lastYearGained, 'money', 2)} last year` }}
			selected={selected} onClick={onClick} />;
	}
	// RN and Revenue: the change in the window, the figure now in the label, the change against last year's change.
	const format = formatOf(measure);
	return <KpiTile label={`${line.title} · ${formatValue(fig.now, format)} now`} value={fig.gained} format={format} signed trend={ty}
		change={fig.vs === null ? undefined : { ...fig.vs, label: `vs ${signedText(fig.lastYearGained, format, 0)} last year` }}
		selected={selected} onClick={onClick} />;
};

// The chart of one row against last year (brief 4.3, "How <value> built up").
export const GuestMixFocusCard: React.FunctionComponent<{ evolution: GuestMixEvolution, table: GuestMixTable, rowKey: string, period: MonthRange,
	measure: EvolutionMeasure, onMeasure: (m: EvolutionMeasure) => void, scope: RevenueScope, vat: VatBasis, onClose: () => void }> = ({
	evolution, table, rowKey, period, measure, onMeasure, scope, vat, onClose,
}) => {
	const line = evolutionLines(evolution, table).find(l => l.key === rowKey);
	if (line === undefined) return null;
	const lastNight = lastDay(period.to);
	const ty = line.thisYear.map((f, i) => evolution.days[i] > lastNight ? null : pointOf(f, measure, scope, vat));
	const ly = line.lastYear.map(f => pointOf(f, measure, scope, vat));
	const fig = focusFigures(ty, ly, measure);
	const format = formatOf(measure);
	const decimals = measure === 'adr' ? 2 : 0;
	const series = [{ id: 'thisYear', label: 'This year', role: 'own' as const, values: ty },
		{ id: 'lastYear', label: 'Last year (same lead time)', role: 'compset' as const, values: ly }];
	return <ChartCard title={`How ${line.title} built up · ${periodLabel(period)}`}
		period={`As it stood on each day ${formatDate(evolution.days[0])} – ${formatDate(evolution.days[evolution.days.length - 1])}`}
		actions={<>
			<ButtonTabs label="Measure of the chart" value={measure} onChange={v => onMeasure(v as EvolutionMeasure)}
				options={[{ value: 'rn', label: 'RN' }, { value: 'rev', label: 'Revenue' }, { value: 'adr', label: 'ADR' }]} />
			<FormButton state="white" onClick={onClose}>Close</FormButton>
		</>}
		table={<DataTable {...lineChartTable({ dates: evolution.days, series, format, decimals })} framed={false} size="normal" ariaLabel={`How ${line.title} built up`} />}
		caption="Last year = last year's same stay dates, as they stood one year earlier on each day (gray).">
		{/* The tile grid of docs/CHARTS.md: a KpiStrip cut these labels at 400 px (UI check of 2026-10-21). */}
		<div aria-label={`${line.title}: figures`} className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
			<KpiTile label="Now" value={fig.now} format={format} decimals={decimals}
				change={fig.change === null ? undefined : { value: fig.change, format: 'percent', label: 'vs last year at this point' }} />
			<KpiTile label="Last year at this point" value={fig.lastYear} format={format} decimals={decimals} />
			{measure !== 'adr' && <KpiTile label="Last 7 days" value={fig.week} format={format} decimals={decimals} />}
			{measure !== 'adr' && <KpiTile label="Last 7 days, last year" value={fig.weekLastYear} format={format} decimals={decimals} />}
		</div>
		<LineChart dates={evolution.days} series={series} format={format} decimals={decimals} ariaLabel={`How ${line.title} built up`} />
	</ChartCard>;
};
